import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import {
  callErrorToast,
  callSuccessToast,
  removeError,
} from "../../../utilities/utilities.ts";
import { CodCollectionsService } from "../../../services/shipment/codCollections.ts";
import {
  CodLiquidationPreview,
  convertCodLiquidationPreviewToCreate,
  validate,
} from "../../../Models/CodLiquidationPreview.ts";
import { cleanObject } from "../../../utilities/utilities.ts";
import { useAppContext } from "../../../AppProvider.jsx";
import { ErrorResponse } from "../../../Models/Error.ts";

const codCollectionsService = new CodCollectionsService();

interface CodLiquidationPreviewProviderProps {
  children: ReactNode;
  liquidationPreview: CodLiquidationPreview;
  callback?: () => void;
}

interface CodLiquidationPreviewContextValue {
  data: Partial<CodLiquidationPreview>;
  error: ErrorResponse<CodLiquidationPreview> | null; // Error response if any request fails
  loader: boolean;
  edit: (
    liquidation: CodLiquidationPreview,
    property: keyof CodLiquidationPreview | null,
    save?: boolean
  ) => void;
  callback?: () => void;
  onSubmit: () => void;
}

const CodLiquidationPreviewContext = createContext<
  CodLiquidationPreviewContextValue | undefined
>(undefined);

const CodLiquidationPreviewProvider: React.FC<
  CodLiquidationPreviewProviderProps
> = ({ children, liquidationPreview, callback }) => {
  const { dictionary } = useAppContext();

  const [data, setData] = useState<Partial<CodLiquidationPreview>>({
    ...liquidationPreview,
    expectedAmount: liquidationPreview.totalAmount,
  });
  const [error, setError] =
    useState<ErrorResponse<CodLiquidationPreview> | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const prevError = useRef<ErrorResponse<CodLiquidationPreview>>(null);

  const edit = (
    liquidation: CodLiquidationPreview,
    property: keyof CodLiquidationPreview | null
  ): void => {
    liquidation = cleanObject(liquidation);
    if (property) {
      removeError(property, error, prevError, setError);
    }
    setData(liquidation);
  };

  const onSubmit = (): void => {
    const validation = validate(data as CodLiquidationPreview);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      return;
    }

    setLoader(true);

    const liquidationPreviewUpdated = convertCodLiquidationPreviewToCreate(
      data as CodLiquidationPreview
    );

    codCollectionsService
      .liquidationSubmit(liquidationPreviewUpdated)
      .then((res) => {
        callSuccessToast(dictionary.messages.save_successful);
        if (callback) {
          callback();
        }
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  return (
    <CodLiquidationPreviewContext.Provider
      value={{
        data,
        error,
        loader,
        edit,
        callback,
        onSubmit,
      }}
    >
      {children}
    </CodLiquidationPreviewContext.Provider>
  );
};

const useCodLiquidationPreviewContext =
  (): CodLiquidationPreviewContextValue => {
    const context = useContext(CodLiquidationPreviewContext);
    if (!context) {
      throw new Error(
        "useCodLiquidationPreviewContext must be used within a CodLiquidationPreviewProvider"
      );
    }
    return context;
  };

export { CodLiquidationPreviewProvider, useCodLiquidationPreviewContext };
